import React, { useState } from 'react';
import { Link2, Copy, Trash2 } from 'lucide-react';

const TextToSlugTool = () => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState({
    lowercase: true,
    separator: '-',
    removeStopWords: false,
    maxLength: 0
  });
  const [output, setOutput] = useState('');
  const [copied, setCopied] = useState(false);

  const stopWords = [
    'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
    'of', 'with', 'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been',
    'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would',
    'could', 'should', 'may', 'might', 'must', 'shall', 'can', 'need'
  ];

  const generateSlug = (text) => {
    if (!text.trim()) {
      setOutput('');
      return;
    }

    let slug = text;

    // Convert to lowercase if option enabled
    if (options.lowercase) {
      slug = slug.toLowerCase();
    }

    // Remove accents/diacritics
    slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Remove stop words if option enabled
    if (options.removeStopWords) {
      const words = slug.split(/\s+/);
      slug = words.filter(word => !stopWords.includes(word.toLowerCase())).join(' ');
    }

    // Replace non-alphanumeric characters with separator
    slug = slug
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, options.separator)
      .replace(new RegExp(`${options.separator}+`, 'g'), options.separator)
      .replace(new RegExp(`^${options.separator}|${options.separator}$`, 'g'), '');

    // Apply max length if set
    if (options.maxLength > 0 && slug.length > options.maxLength) {
      slug = slug.substring(0, options.maxLength);
      // Don't end with separator
      slug = slug.replace(new RegExp(`${options.separator}$`), '');
    }

    setOutput(slug);
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    generateSlug(e.target.value);
  };

  const handleOptionChange = (key, value) => {
    const newOptions = { ...options, [key]: value };
    setOptions(newOptions);
    // Regenerate with new options
    setTimeout(() => {
      let slug = input;
      if (newOptions.lowercase) slug = slug.toLowerCase();
      slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (newOptions.removeStopWords) {
        const words = slug.split(/\s+/);
        slug = words.filter(word => !stopWords.includes(word.toLowerCase())).join(' ');
      }
      slug = slug
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, newOptions.separator)
        .replace(new RegExp(`${newOptions.separator}+`, 'g'), newOptions.separator)
        .replace(new RegExp(`^${newOptions.separator}|${newOptions.separator}$`, 'g'), '');
      if (newOptions.maxLength > 0 && slug.length > newOptions.maxLength) {
        slug = slug.substring(0, newOptions.maxLength).replace(new RegExp(`${newOptions.separator}$`), '');
      }
      setOutput(slug);
    }, 0);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const samples = [
    'How to Create SEO-Friendly URLs in 2024!',
    '10 Best Practices for Web Development',
    "The Ultimate Guide to React & Node.js",
    'Café & Restaurant: Tips for Success!'
  ];

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Text to Convert</label>
        <input
          type="text"
          value={input}
          onChange={handleInputChange}
          placeholder="Enter your title or text..."
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {samples.map((sample, i) => (
          <button
            key={i}
            onClick={() => { setInput(sample); generateSlug(sample); }}
            className="text-xs px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 truncate max-w-xs"
          >
            {sample}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-4 gap-4 p-4 bg-gray-800/50 rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={options.lowercase}
            onChange={(e) => handleOptionChange('lowercase', e.target.checked)}
            className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600"
          />
          <span className="text-sm text-gray-300">Lowercase</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={options.removeStopWords}
            onChange={(e) => handleOptionChange('removeStopWords', e.target.checked)}
            className="w-4 h-4 rounded bg-gray-700 border-gray-600 text-blue-600"
          />
          <span className="text-sm text-gray-300">Remove stop words</span>
        </label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-300">Separator:</span>
          <select
            value={options.separator}
            onChange={(e) => handleOptionChange('separator', e.target.value)}
            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white"
          >
            <option value="-">Hyphen (-)</option>
            <option value="_">Underscore (_)</option>
            <option value=".">Dot (.)</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-300">Max length:</span>
          <input
            type="number"
            value={options.maxLength}
            onChange={(e) => handleOptionChange('maxLength', parseInt(e.target.value) || 0)}
            min="0"
            max="200"
            placeholder="0 = unlimited"
            className="w-20 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white"
          />
        </div>
      </div>

      {output && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-300">Generated Slug</label>
            <span className="text-xs text-gray-500">{output.length} characters</span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-900 rounded">
            <code className="text-lg text-green-400 break-all">{output}</code>
            <button
              onClick={copyToClipboard}
              className={`ml-3 p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            Full URL preview: <span className="text-blue-400">https://example.com/{output}</span>
          </div>
        </div>
      )}

      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <h3 className="text-sm font-medium text-blue-300 mb-2">SEO-Friendly URL Tips</h3>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Keep slugs short and descriptive (50-60 chars ideal)</li>
          <li>• Use hyphens (-) to separate words</li>
          <li>• Include your target keyword</li>
          <li>• Avoid numbers unless necessary (like year)</li>
          <li>• Use lowercase letters only</li>
        </ul>
      </div>

      <button
        onClick={() => { setInput(''); setOutput(''); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default TextToSlugTool;
